import { type CallhomeCamera, type CallhomeStream, type DeviceEntity } from '@solinkcorp/api-types';
import {
  userHasFeature,
  type UserSession,
  type UserWithFeatures,
} from '@solinkcorp/authentication';
import { type VideoSegment, type VideoUrlProps } from '@solinkcorp/playlist-parser';
import settings from '@solinkcorp/settings';
import {
  type AuthCredentials,
  type AuthLocation,
  getLocalCredentials,
} from '@solinkcorp/vd-common/lib/esm/auth/authUtils';
import { type IEvent } from 'clients/PlatformApi';
import { type HeatmapOptions } from 'components/VideoPlayer/components/Heatmap/heatmapState';
import {
  THUMBNAIL_LEVEL_AD,
  type ThumbnailLevel,
} from 'components/VideoPlayer/components/PlayerThumbnail/VideoThumbnail';
import { type PlayingCamera } from 'components/VideoPlayer/controller/PlayingCamerasController';
import { type ScaleLinear } from 'd3-scale';
import { type Immutable } from 'immer';
import { type SelectedStream, type StreamQuality } from 'src/core/video/types';
import { LocationStore } from 'stores/LocationStore';
import type { SetOptional } from 'type-fest';
import * as CameraUtil from 'utils/CameraUtil';
import { _debug, shouldLog } from 'utils/LoggingUtil';
import { type PlayerType } from 'utils/PlayerUtil';
import {
  type CachedPlaylist,
  type PlaylistCache,
  type PlaylistResult,
} from 'utils/PlaylistCache/PlaylistCache';
import { type SelectedPlayListSource, type VideoDiscoverySite } from 'utils/types';
import { getSupportedPlayerTypes, isMP4Available } from './supportedPlayers';

/**
 * This is the set of all video player types that can play archive video.  If a
 * player can handle both live and archive video, it can be here and in
 * LIVE_VIDEO_PLAYERS below.
 */
const ARCHIVE_VIDEO_PLAYERS = ['HLS', 'ConnectHLS'] as const;
const ARCHIVE_VIDEO_PLAYERS_SET = new Set(ARCHIVE_VIDEO_PLAYERS);
export type ArchiveVideoPlayerType = (typeof ARCHIVE_VIDEO_PLAYERS)[number];

/** The set of all video players that can play live video. */
const LIVE_VIDEO_PLAYERS = ['HLS', 'ConnectHLS', 'WebRTC'] as const;
const LIVE_VIDEO_PLAYERS_SET = new Set(LIVE_VIDEO_PLAYERS);
export type LiveVideoPlayerType = (typeof LIVE_VIDEO_PLAYERS)[number];

export type VideoPlayerType = LiveVideoPlayerType | ArchiveVideoPlayerType;

export const VIDEO_PLAYERS = [
  ...new Set((ARCHIVE_VIDEO_PLAYERS as readonly string[]).concat(LIVE_VIDEO_PLAYERS)),
] as readonly VideoPlayerType[];

// Max playback rate for 1 camera
const DEFAULT_MAX_PLAYBACK_RATE = 16;
// Available options for playback rates
const DEFAULT_PLAYBACK_RATES = [1, 2, 4, 8, 16];

export const GLOBAL_CAMERA_TAG_CONNECT_HLS = 'CONNECT_HLS';

/**
 * Returns true if the string represents a live video player type.
 */
export function isLiveVideoPlayerType(s: string | undefined): s is LiveVideoPlayerType {
  return !!s && LIVE_VIDEO_PLAYERS_SET.has(s as LiveVideoPlayerType);
}

/**
 * Returns true if the string represents an archive video player type.
 */
export function isArchiveVideoPlayerType(s: string | undefined): s is ArchiveVideoPlayerType {
  return !!s && ARCHIVE_VIDEO_PLAYERS_SET.has(s as ArchiveVideoPlayerType);
}

export function isVideoPlayerType(s: string | undefined): s is VideoPlayerType {
  return (!!s && isArchiveVideoPlayerType(s)) || isLiveVideoPlayerType(s);
}

export interface SegmentData extends CachedPlaylist {
  readonly id: string;
  readonly streamId: string;
  readonly streamUrlProps: Immutable<VideoUrlProps>;
  readonly start: number;
  readonly end: number;
  readonly source: 'local' | 'cloud';
}

const getCloudPlayList = (params: {
  quality: StreamQuality;
  location: { id: string };
  tenantId: string;
  camId: string;
  start: number;
  end: number;
  authQueryParams?: { [key: string]: string };
  thumbnailLevel?: ThumbnailLevel;
}): string => {
  const {
    quality,
    location: { id: locationId },
    tenantId,
    camId,
    start: rawStart,
    end: rawEnd,
    authQueryParams,
    thumbnailLevel,
  } = params;
  const start = Math.floor(rawStart / 1000) * 1000;
  const end = Math.floor(rawEnd / 1000) * 1000;

  const query = new URLSearchParams({
    ...authQueryParams,
    start: `${start}`,
    end: `${end}`,
    merge: 'true',
    motiongrid: 'true',
    // Ask connect-vms to insert `#EXT-X-DISCONTINUITY` markers into the
    // playstream, as required.
    discontinuityTag: 'true',
    setCookie: '0',
  });
  if (thumbnailLevel) {
    // Add 15 minutes to the start time to ensure we get the thumbnail
    query.set('start', (start - 15 * 60 * 1000).toString());
    query.append('level', thumbnailLevel.toString());
  }
  switch (quality) {
    case 'ad':
      query.append('level', THUMBNAIL_LEVEL_AD.toString());
      break;
  }

  return (
    `${settings.cloud_vms_host}` +
    `/tenants/${tenantId}` +
    `/devices/${locationId}` +
    `/cameras/${camId}/playlist.m3u8?${query.toString()}`
  );
};

export const getLocalM3U8 = (params: {
  quality: StreamQuality;
  deviceUrl: string;
  camId: string;
  streamId: string;
  start: number;
  end: number;
  selectedPlayListSource: SelectedPlayListSource;
  authQueryParams?: { [key: string]: string };
  vtt?: boolean;
  thumbnailLevel?: ThumbnailLevel;
}): string => {
  const {
    quality,
    deviceUrl,
    camId,
    streamId,
    start: rawStart,
    end: rawEnd,
    selectedPlayListSource,
    authQueryParams,
    vtt,
    thumbnailLevel,
  } = params;
  const start = Math.floor(rawStart / 1000) * 1000;
  const end = Math.floor(rawEnd / 1000) * 1000;

  const query = new URLSearchParams({
    ...authQueryParams,
    stream: streamId,
    motiongrid: 'true',
    ai: 'true',
    // Ask connect-vms to insert `#EXT-X-DISCONTINUITY` markers into the
    // playstream, as required.
    discontinuityTag: 'true',
    source: selectedPlayListSource, // TODO: Why do we pass this to the client?
    begin: new Date(start).getTime().toString(),
    end: new Date(end).getTime().toString(),
    setCookie: '0',
  });
  if (thumbnailLevel) {
    // Add 15 minutes to the start time to ensure we get the thumbnail
    query.set('begin', (start - 15 * 60 * 1000).toString());
    query.append('level', thumbnailLevel.toString());
  }

  /*
   * This paramter (merge = true) merges the SD and HD playlists together.
   * SD will be used wherever there is no motion grid data.
   * When merge=true, no motion grid data will be returned, regardless if motiongrid is set to true.
   * We always use the SD stream motiongrid data to show on the timeline
   */
  switch (quality) {
    case 'hd':
      query.append('merge', 'true');
      break;
    case 'ad':
      query.append('level', THUMBNAIL_LEVEL_AD.toString());
      break;
  }

  if (vtt) {
    query.append('vtt', 'true');
    query.append('showLocation', 'true');
  }

  return `${deviceUrl}/cameras/${camId}/video.m3u8?${query.toString()}`;
};

const isStreamAvailable = (stream: CallhomeStream, target: number | Date) => {
  if (!stream) {
    return false;
  }

  //
  // local vms returns string for the earliestSegmentDate
  const earliestSegmentDate = toNumber(stream.earliestSegmentDate);

  //
  // should probably be target >= parseInt(earliestSegmentDate) && target <= parseInt(latestSegmentDate),
  // but the latest turns out to prevent the video from playing at times, not sure why
  const hasCoverage = earliestSegmentDate && new Date(target).valueOf() >= earliestSegmentDate;

  if (shouldLog()) {
    console.group('Stream Selection');
    _debug('requesting to start video at: ', new Date(target));
    _debug(
      'stream earliestSegmentDate: ',
      earliestSegmentDate ? new Date(earliestSegmentDate) : 'none',
    );
    console.groupEnd();
  }
  return hasCoverage;
};

const sortStreams = ({
  prioritizeMotionStream,
  streams,
  quality,
}: {
  streams: readonly CallhomeStream[];
  prioritizeMotionStream: boolean;
  quality?: StreamQuality;
}) => {
  const copiedStreams = [...streams];
  if (prioritizeMotionStream) {
    // Prioritize streams with isMotionOn
    return copiedStreams.sort((a, b) => {
      if (a.isMotionOn && !b.isMotionOn) return -1;
      if (!a.isMotionOn && b.isMotionOn) return 1;
      return 0;
    });
  } else if (quality === 'hd') {
    // Sort by average_bps in descending order for best quality
    return copiedStreams.sort((a, b) => (b.average_bps || 0) - (a.average_bps || 0));
  }
  // Sort by average_bps in ascending order for lowest quality
  return copiedStreams.sort((a, b) => (a.average_bps || 0) - (b.average_bps || 0));
};

export const getThumbnailStream = (
  streams: readonly CallhomeStream[],
): CallhomeStream | undefined => {
  return streams.find(({ earliestDerivedSegmentDate }) => earliestDerivedSegmentDate);
};

export const selectStream = (args: {
  location: { cloudOnly?: boolean; type?: string } | undefined;
  streams: readonly CallhomeStream[];
  targetStartTime: number | Date;
  quality: StreamQuality;
  prioritizeMotionStream?: boolean;
  selectedPlayListSource?: SelectedPlayListSource;
  isDeviceOffline?: boolean;
  thumbnailLevel?: ThumbnailLevel;
}): SelectedStream | undefined => {
  const {
    location,
    streams,
    targetStartTime,
    quality,
    selectedPlayListSource,
    isDeviceOffline,
    prioritizeMotionStream = false,
    thumbnailLevel,
  } = args;

  if (!location) return undefined;

  if (thumbnailLevel) {
    const stream = getThumbnailStream(streams);
    if (stream) {
      return { stream, source: 'local', quality };
    }
  }

  const availableStreams = streams.slice();

  const sortedStreams = sortStreams({
    streams: availableStreams,
    prioritizeMotionStream,
    quality,
  });

  let selectedStream: SelectedStream | undefined;
  let i = 0;

  const cloudOnly = location.cloudOnly || selectedPlayListSource === 'cloudonly' || isDeviceOffline;
  const localOnly = location.type === 'container' || selectedPlayListSource === 'localonly';

  if (!cloudOnly) {
    //
    // check local only for the first pass
    //
    while (i < sortedStreams.length && !selectedStream) {
      const stream = sortedStreams[i];

      if (shouldLog()) {
        console.group(`LOCAL-stream-${i}`);
      }
      _debug('earlistSegmentDate: ', toDate(stream.earliestSegmentDate));

      if (isStreamAvailable(stream, targetStartTime)) {
        selectedStream = { stream, source: 'local', quality };
        _debug(' found local stream ', selectedStream);
      }

      i++;
      if (shouldLog()) {
        console.groupEnd();
      }
    }
  }

  if (!selectedStream && !localOnly) {
    i = 0;
    //
    // check cloud if no local stream available
    //
    while (i < availableStreams.length && !selectedStream) {
      const stream = availableStreams[i];

      if (shouldLog()) {
        console.group(`CLOUD-stream-${i}`);
      }
      _debug('earlistSegmentDate: ', toDate(stream.earliestSegmentDate));

      // Always pick the cloud stream, even if `isStreamAvailable()` would return false.
      // `isStreamAvailable` is based on the stream's `earliestSegmentDate`, but
      // that is based entirely on retention, and we can have saved clips in
      // the cloud that are much older than the `earliestSegmentDate`.
      selectedStream = { stream, source: 'cloud', quality };
      _debug(' found cloud stream ', selectedStream);

      i++;
      if (shouldLog()) {
        console.groupEnd();
      }
    }
  }

  _debug('result ::: ', selectedStream);
  return selectedStream;
};

export interface RecordingUrlParams {
  user: {
    accessToken: string;
    thirdPartyCookiesSet: boolean;
    customerId: string;
  };
  quality: StreamQuality;
  location: AuthLocation;
  streamId: string;
  source: 'cloud' | 'local';
  start: number;
  end: number;
  vmsCameraId: string;
  selectedPlayListSource: SelectedPlayListSource;
  vtt?: boolean;
  thumbnailLevel?: ThumbnailLevel;
  videoUrl?: string; // Optional: alarm.m3u8 URL for alarm events (VerifEye3)
}

export const generateRecordingURL = (params: RecordingUrlParams): VideoUrlProps | undefined => {
  const {
    user,
    quality,
    location,
    streamId,
    source,
    start: rawStart,
    end: rawEnd,
    vmsCameraId,
    selectedPlayListSource,
    vtt,
    thumbnailLevel,
    videoUrl,
  } = params;
  const start = Math.floor(rawStart / 1000) * 1000;
  const end = Math.floor(rawEnd / 1000) * 1000;

  // GUARD: If videoUrl is provided (e.g., alarm.m3u8 from alarm events), use it directly
  // regular playback is unaffected
  if (videoUrl) {
    try {
      const url = new URL(videoUrl);

      // Force motion grid parameter to true
      url.searchParams.set('motiongrid', 'true');

      return {
        url: url.toString(),
      };
    } catch (error) {
      if (shouldLog()) {
        _debug('Invalid videoUrl passed to generateRecordingURL', { videoUrl, error });
      }
      // Fall through to existing logic if videoUrl is invalid
    }
  }

  // EXISTING LOGIC BELOW - No changes for regular users
  const restrictedCameras = LocationStore.getRestrictedCameras();

  if (
    !restrictedCameras ||
    restrictedCameras.length === 0 ||
    (restrictedCameras.length > 0 && restrictedCameras.includes(vmsCameraId))
  ) {
    const deviceUrl = location.accessibleAddress;

    const camId = vmsCameraId;

    //
    // CLOUD
    //
    if (source === 'cloud' || !deviceUrl) {
      const { authHeaders, authQueryParams } = getCredentials(user, { type: 'cloud' });
      const url = getCloudPlayList({
        quality,
        location,
        camId,
        tenantId: user.customerId,
        start,
        end,
        authQueryParams,
        thumbnailLevel,
      });
      return { url, authHeaders, authQueryParams };
    }
    //
    //  LOCAL
    //

    const { authHeaders, authQueryParams } = getCredentials(user, {
      type: 'local',
      location,
    });
    const url = getLocalM3U8({
      quality,
      deviceUrl,
      camId,
      streamId,
      start,
      end,
      selectedPlayListSource,
      authQueryParams,
      vtt,
      thumbnailLevel,
    });
    return { url, authHeaders, authQueryParams };
  }

  return undefined;
};

export const getFirstStream = (camera: CallhomeCamera | undefined): CallhomeStream => {
  const streams = camera?.streams || [];
  return streams[0] || null;
};

function getM3U8FromCloud(
  recordingParams: RecordingUrlParams,
  playlistCache: PlaylistCache,
): [VideoUrlProps, Promise<PlaylistResult>] {
  const cloudUrlProps = generateRecordingURL({
    ...recordingParams,
    source: 'cloud',
  });

  if (!cloudUrlProps) {
    throw new Error(`Need cloudVMS URL`);
  }

  return [cloudUrlProps, playlistCache.loadPlaylist(cloudUrlProps)];
}

export function getSegmentsId(params: SetOptional<RecordingUrlParams, 'user'>): string {
  return `${params.location.id}-${params.vmsCameraId}-${params.streamId}-${params.start}-${params.end}`;
}

export async function getRecordingSegmentsFromM3U8(params: {
  recordingParams: RecordingUrlParams;
  playlistCache: PlaylistCache;
  /** If provided, call this function to check if the device is online. */
  checkDeviceOnline?: (location: AuthLocation) => Promise<boolean>;
}): Promise<{
  isDeviceOnline: boolean | undefined;
  segmentData: SegmentData;
  source: 'cloud' | 'local';
}> {
  const { recordingParams, playlistCache, checkDeviceOnline } = params;
  const {
    location: { accessibleAddress: deviceUrl },
  } = recordingParams;

  let urlProps = generateRecordingURL(recordingParams);

  if (!urlProps) {
    throw new Error('Unable to get URL for recording.');
  }

  const isUrlLocal = !!(deviceUrl && urlProps.url?.startsWith(deviceUrl));

  const { location, selectedPlayListSource } = recordingParams;

  const isLocalOnly = selectedPlayListSource === 'localonly';

  const abortController = new AbortController();
  let isDeviceOnline: boolean | undefined;
  let recordingResponsePromise: Promise<PlaylistResult>;
  let source: 'cloud' | 'local';

  if (!isUrlLocal || isLocalOnly || !checkDeviceOnline) {
    recordingResponsePromise = playlistCache.loadPlaylist(urlProps);
    // If fetching the local playlist succeeds, and `isUrlLocal`, then the device must be online.
    isDeviceOnline = isUrlLocal ? true : undefined;
    source = isUrlLocal ? 'local' : 'cloud';
  } else {
    // Before we find out if the device is online or not, fire off a request
    // for the M3U8 file.  In the "success case" this will come back with
    // an answer while we're trying to figure out if the device is online or not.
    let localDone = false;
    source = 'local';
    recordingResponsePromise = playlistCache.loadPlaylist(urlProps, {
      signal: abortController.signal,
    });
    recordingResponsePromise = recordingResponsePromise.then((result) => {
      localDone = true;
      return result;
    });

    // Add a `catch` to the `recordingResponsePromise`, because we might swap it
    // out below for the cloud one, and if that happens we don't want a
    // "UnhandledPromiseRejectionWarning".  Note that if we await this
    // `recordingResponsePromise` below without swapping it out, we'll still
    // throw the error.
    recordingResponsePromise.catch(() => void 0);

    // Assume the device is online unless the below fails.
    isDeviceOnline = await checkDeviceOnline(location);
    if (localDone) {
      // While we were checking to see if the device was online, our
      // local request succeeded.  We're all done.
      isDeviceOnline = true;
    } else if (!isDeviceOnline) {
      // Cancel the local VMS request and make request to cloud VMS.
      isDeviceOnline = false;
      abortController.abort();
      console.debug(`Local VMS request canceled because the device is offline`);

      source = 'cloud';
      [urlProps, recordingResponsePromise] = getM3U8FromCloud(
        recordingParams as RecordingUrlParams,
        playlistCache,
      );
      console.debug(`Successfully switched request from device to cloud VMS.`);
    }
  }

  const data = await recordingResponsePromise;

  const segmentData = {
    ...data.playlist,
    id: getSegmentsId(recordingParams),
    streamId: recordingParams.streamId,
    streamUrlProps: urlProps,
    start: recordingParams.start,
    end: recordingParams.end,
    source,
  };

  return {
    isDeviceOnline,
    segmentData,
    source,
  };
}

/**
 * Get fixed recording segments from solink.direct
 */
export async function getFixedRecordingSegmentsFromM3U8(params: {
  recordingParams: SetOptional<RecordingUrlParams, 'user'>;
  playlistCache: PlaylistCache;
  fixedPlaylistSegmentsUrlProps: VideoUrlProps;
}): Promise<{
  isDeviceOnline: boolean | undefined;
  segmentData: SegmentData;
  source: 'local';
}> {
  const { recordingParams, playlistCache, fixedPlaylistSegmentsUrlProps } = params;

  const recordingResponsePromise = playlistCache.loadPlaylist(fixedPlaylistSegmentsUrlProps);

  const data = await recordingResponsePromise;

  const segmentData = {
    ...data.playlist,
    id: getSegmentsId(recordingParams),
    streamId: recordingParams.streamId,
    streamUrlProps: fixedPlaylistSegmentsUrlProps,
    start: recordingParams.start,
    end: recordingParams.end,
    source: 'local' as const,
  };

  return {
    isDeviceOnline: true,
    segmentData,
    source: 'local',
  };
}

/**
 * Returns the earliest segment start from a collection of segments, or
 * undefined if no segments are passed in.
 */
export const getEarliestSegmentStart = ({
  segments = [],
}: {
  segments?: readonly VideoSegment[];
}): number | undefined => {
  if (segments.length === 0) {
    return undefined;
  }

  const { start: earliestSegment } = segments[0];
  return earliestSegment;
};

function toNumber(value: string | number): number;
function toNumber(value: undefined): undefined;
function toNumber(value: string | number | undefined): number | undefined;
function toNumber(value: string | number | undefined) {
  if (value === undefined) {
    return undefined;
  } else if (typeof value === 'string') {
    return parseInt(value, 10);
  }
  return value;
}

function toDate(value: string | number): Date;
function toDate(value: undefined): undefined;
function toDate(value: string | number | undefined): Date | undefined;
function toDate(value: string | number | undefined) {
  if (value === undefined) {
    return undefined;
  } else if (typeof value === 'string') {
    return new Date(parseInt(value, 10));
  }
  return new Date(value);
}

const AUDIO_SAMPLING_RATE_8000 = 8000;
const AUDIO_SAMPLING_RATE_16000 = 16000;
const AUDIO_ENCODING_PCM_FAMILY = 'pcm';
const AUDIO_ENCODING_PCM_MULAW = 'pcm_mulaw';
const AUDIO_ENCODING_PCM_ALAW = 'pcm_alaw';
const AUDIO_ENCODING_G726LE = 'g726le';
const AUDIO_ENCODING_AAC = 'aac';

const FORCE_CONNECT_HLS_CAMERAS: {
  manufacturer: string;
  model: string;
  audioSamplingRate: number;
  audioEncoding: string[];
}[] = [
  {
    manufacturer: 'Samsung Techwin',
    model: 'QND-6012R',
    audioSamplingRate: AUDIO_SAMPLING_RATE_8000,
    audioEncoding: [AUDIO_ENCODING_PCM_MULAW],
  },
  {
    manufacturer: 'Samsung Techwin',
    model: 'QNV-6082R',
    audioSamplingRate: AUDIO_SAMPLING_RATE_8000,
    audioEncoding: [AUDIO_ENCODING_PCM_MULAW],
  },
  {
    manufacturer: 'Samsung Techwin',
    model: 'QND-6022R',
    audioSamplingRate: AUDIO_SAMPLING_RATE_8000,
    audioEncoding: [AUDIO_ENCODING_G726LE],
  },
  {
    manufacturer: 'DigitalWatchdog',
    model: 'DWC-MT95Wi28TW',
    audioSamplingRate: AUDIO_SAMPLING_RATE_8000,
    audioEncoding: [AUDIO_ENCODING_PCM_ALAW, AUDIO_ENCODING_PCM_MULAW],
  },
];

/**
 * Hls.js is not able to play video for certain cameras
 * that have a low sampling (8000) with the pcm_mulaw audio encoding.
 * Known camera models that cannot load video properly are:
 * Wisenet QND-6012R and QNV-6082R.
 * Check the audio settings on the stream and fallback to ConnectHLS,
 * for a sampling rate equal to 8000 with pcm_mulaw encoding for Wisenet QND-6012R and QNV-6082R
 */
function shouldFallbackToConnectHls(
  user: UserWithFeatures,
  camera: {
    manufacturer?: string;
    model?: string;
    audioParams?: { enabled?: boolean };
  },
  selectedStream: SelectedStream,
  playingLive: boolean,
): boolean {
  const { manufacturer, model, audioParams } = camera;

  let fallback = false;

  if (!selectedStream.stream?.audioStats) {
    fallback = false;
  } else {
    const {
      audioStats: { codec, s_rate },
    } = selectedStream.stream;

    if (manufacturer && model) console.info(`Camera: ${manufacturer} ${model}`);
    if (codec) console.info(`Audio Encoding: ${codec}`);
    if (s_rate && s_rate !== -1) console.info(`Sampling Rate: ${s_rate}`);

    const incompatibleCameraWithHlsJs = FORCE_CONNECT_HLS_CAMERAS.some(
      (camera) =>
        camera.manufacturer === manufacturer &&
        camera.model === model &&
        camera.audioSamplingRate === s_rate &&
        camera.audioEncoding.includes(codec),
    );

    // when the camera manufacurer and model is not available, we switch to ConnectHLS
    // if the audio encoding belongs to the pcm family or is aac and has a low sampling rate
    const cameraUnknown = !manufacturer || !model;
    const incompatibleAudioStatsWithHlsJs =
      !!audioParams?.enabled &&
      (codec.includes(AUDIO_ENCODING_PCM_FAMILY) || codec === AUDIO_ENCODING_AAC) &&
      s_rate < AUDIO_SAMPLING_RATE_16000;

    fallback = incompatibleCameraWithHlsJs || (cameraUnknown && incompatibleAudioStatsWithHlsJs);
  }

  const mp4Enabled = isMP4Available(user, selectedStream);

  return (
    !userHasFeature(user, 'forceHLS') &&
    (fallback || (playingLive && !mp4Enabled && !!audioParams?.enabled))
  );
}

export function cameraHasConnectHLSTag(cameraTags: string[]): boolean {
  return cameraTags.includes(GLOBAL_CAMERA_TAG_CONNECT_HLS);
}

/**
 * Determine which player we should use for playing live.
 */
export function selectPlayerType(params: {
  user: UserSession;
  location: VideoDiscoverySite;
  locationIsLocal: boolean;
  camera: CallhomeCamera;
  selectedStream: SelectedStream;
  preferredVideoPlayer: VideoPlayerType | undefined;
  quick: boolean;
  playingLive: boolean;
  numberOfPlayingCameras: number;
  device: DeviceEntity | undefined;
}): VideoPlayerType {
  const { user, camera, selectedStream, preferredVideoPlayer, playingLive } = params;
  const { source } = selectedStream;

  if (source === 'cloud') {
    // Always use HLS for playback from cloud.
    return 'HLS';
  }

  let result: LiveVideoPlayerType;

  const allowConnectHLS = userHasFeature(user, 'allowConnectHLS');

  const playerTypes = getSupportedPlayerTypes(params);

  if (
    preferredVideoPlayer &&
    isLiveVideoPlayerType(preferredVideoPlayer) &&
    playerTypes[preferredVideoPlayer].supported
  ) {
    result = preferredVideoPlayer;
  } else if (playerTypes.WebRTC.supported) {
    result = 'WebRTC';
  } else if (
    playerTypes.ConnectHLS.supported &&
    ((allowConnectHLS && shouldFallbackToConnectHls(user, camera, selectedStream, playingLive)) ||
      cameraHasConnectHLSTag(camera.cameraTagIds))
  ) {
    result = 'ConnectHLS';
  } else {
    result = 'HLS';
  }

  return result;
}

export function getCredentials(
  user:
    | {
        accessToken: string;
        thirdPartyCookiesSet: boolean;
      }
    | undefined,
  params: { type: 'local'; location: AuthLocation } | { type: 'cloud' },
): AuthCredentials {
  if (!user) {
    return {
      authHeaders: undefined,
      authQueryParams: undefined,
      type: 'cookie',
    };
  }
  const jwt = user.accessToken;

  if (params.type === 'local') {
    const useCookies = user.thirdPartyCookiesSet;
    return getLocalCredentials(params.location, jwt, useCookies);
  }
  return {
    type: 'cookie',
    authHeaders: undefined,
    authQueryParams: undefined,
  };
}

/** Converts wall-clock time to player-time. */
export function wallClockToPlayerTime(ttf: ScaleLinear<number, number>, clockTime: number): number {
  return ttf.invert(clockTime) / 1000;
}

/** Converts the player-time into wall-clock time. */
export function playerTimeToWallClockTime(
  ttf: (playerTime: number) => number,
  playerTime: number,
): number {
  return Math.round(ttf(playerTime * 1000));
}

/**
 * Sums up all the buffered data in a TimeRanges.
 */
export function getTotalBufferedSeconds(buffered: TimeRanges): number {
  let totalBufferedSeconds = 0;
  for (let i = 0; i < buffered.length; i++) {
    totalBufferedSeconds += buffered.end(i) - buffered.start(i);
  }
  return totalBufferedSeconds;
}

export function getMaxPlaybackRate(playingCameras: readonly PlayingCamera[]): number {
  const possiblePlaybackRates = DEFAULT_PLAYBACK_RATES.filter(
    (rate) => rate <= DEFAULT_MAX_PLAYBACK_RATE / playingCameras.length,
  );
  return possiblePlaybackRates[possiblePlaybackRates.length - 1];
}

export function isSeekTimeBeforeVideoStartDate(seekTime: number, videoStartDate: Date): boolean {
  return videoStartDate.valueOf() > seekTime;
}

export function isSeekTimeAfterVideoEndDate(seekTime: number, videoEndDate: Date): boolean {
  return seekTime >= videoEndDate.valueOf();
}

export function getInitialMasterTime(
  playingLiveByDefault: boolean | undefined,
  videoStartTime: number | undefined,
  pinnedEvent: IEvent | undefined,
  filterDate: number,
  playerType: PlayerType,
): number {
  let masterTime: number;

  if (playingLiveByDefault) {
    return Date.now();
  } else if (videoStartTime) {
    masterTime = videoStartTime;
  } else if (playerType !== 'videoPage' && pinnedEvent?.startTime) {
    masterTime = pinnedEvent.startTime.getTime();
  } else {
    masterTime = filterDate;
  }

  return masterTime;
}

/**
 * True if none of the alternate visualization modes that visually conflict with ANY
 * viewport overlay chrome (360 dome or PTZ) are active. Unlike
 * `areGeneral360VRRequirementsMet`, this deliberately excludes the pinhole-view
 * preferences, which are a 360-only dewarp/flat toggle with no meaning for PTZ.
 */
export function areSharedOverlayRequirementsMet({
  isMotionSearchOn,
  isMotionZonesOn,
  showHeatmaps,
}: {
  isMotionSearchOn: boolean;
  isMotionZonesOn: boolean;
  showHeatmaps: HeatmapOptions | undefined;
}): boolean {
  return !isMotionSearchOn && !isMotionZonesOn && !showHeatmaps;
}

export function areGeneral360VRRequirementsMet({
  isMotionSearchOn,
  isMotionZonesOn,
  showHeatmaps,
  showPinholeView,
  shouldDefaultToPinholeViewMultiplay,
}: {
  isMotionSearchOn: boolean;
  isMotionZonesOn: boolean;
  showHeatmaps: HeatmapOptions | undefined;
  showPinholeView: boolean | undefined;
  shouldDefaultToPinholeViewMultiplay: boolean;
}): boolean {
  return (
    areSharedOverlayRequirementsMet({ isMotionSearchOn, isMotionZonesOn, showHeatmaps }) &&
    !showPinholeView &&
    !shouldDefaultToPinholeViewMultiplay
  );
}

export function is360PlayerVRRequirementMet({
  camera,
}: {
  camera: CallhomeCamera | undefined;
}): boolean {
  return !!camera && CameraUtil.is360(camera);
}
